import useSetProposalValidForDays from '@/api/mutations/proposals/useSetProposalValidForDays';
import { usePermissions } from '@/hooks/usePermissions';
import { zodResolver } from '@hookform/resolvers/zod';
import { Field, NumberInput } from '@payaca/ambrosia';
import { useDebounceCallback } from '@payaca/hooks';
import { JobPermissions } from '@payaca/permissions/jobs/job.permissions';
import { FC, useEffect, useId, useRef } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

// Mirrors the backend gate (setProposalValidForDays).
const VALID_FOR_DAYS_SCHEMA = z.object({
  validForDays: z
    .number()
    .int('Enter a whole number of days')
    .min(0, 'Enter a whole number of days (0 or more)')
    .nullable()
    .refine((value) => value !== null, {
      message: 'Enter how many days the proposal stays valid',
    }),
});

type FormValues = z.infer<typeof VALID_FOR_DAYS_SCHEMA>;

const SAVE_DEBOUNCE_MS = 500;

export type ProposalValidForDaysFieldProps = {
  proposalId: string;
  /** The Proposal's current valid-for days, or null when unset. */
  initialValidForDays: number | null;
  /** The send flow gates on this to block sending without a validity value. */
  onValidityChange?: (isValid: boolean) => void;
};

/**
 * Autosaves via its own GraphQL mutation (debounced, flushed on blur) rather than
 * the send form, so it renders an isolated FormProvider — never a nested `<form>`.
 */
const ProposalValidForDaysField: FC<ProposalValidForDaysFieldProps> = (
  props
) => {
  const { proposalId, initialValidForDays, onValidityChange } = props;

  const inputId = useId();

  const { userHasRequiredPermission } = usePermissions();
  const canEdit = userHasRequiredPermission([
    JobPermissions.EDIT_JOB_VALID_FOR_DUE_IN_DAYS,
  ]);

  const { mutate } = useSetProposalValidForDays({ proposalId });

  const methods = useForm<FormValues>({
    mode: 'onChange',
    // The debounced save calls handleSubmit on every settle, which would
    // otherwise steal focus back on blurring an invalid value.
    shouldFocusError: false,
    resolver: zodResolver(VALID_FOR_DAYS_SCHEMA),
    defaultValues: {
      validForDays: initialValidForDays,
    },
  });

  // Derived from the live value rather than the persisted Proposal, which stays
  // stale while a blank entry is left unsaved.
  const currentValue = methods.watch('validForDays');
  const isValueValid =
    currentValue !== null &&
    Number.isInteger(currentValue) &&
    currentValue >= 0;
  useEffect(() => {
    onValidityChange?.(isValueValid);
  }, [isValueValid, onValidityChange]);

  // So typing back to the saved value doesn't fire a redundant mutation.
  const lastSavedRef = useRef<number | null>(initialValidForDays);

  const debouncedSave = useDebounceCallback(
    () => {
      void methods.handleSubmit(({ validForDays }) => {
        if (validForDays === null) return;
        if (validForDays === lastSavedRef.current) return;
        lastSavedRef.current = validForDays;
        mutate({ proposalId, validForDays });
      })();
    },
    SAVE_DEBOUNCE_MS,
    { flushOnUnmount: true }
  );

  return (
    <FormProvider {...methods}>
      <Field id={inputId} name="validForDays" className="mb-4">
        <Field.Label>Valid for (days)</Field.Label>

        <Controller<FormValues, 'validForDays'>
          name="validForDays"
          render={({ field }) => (
            <NumberInput
              min={0}
              step={1}
              value={field.value}
              disabled={!canEdit}
              onChange={(value) => {
                field.onChange(value);
                debouncedSave();
              }}
              onBlur={() => {
                field.onBlur();
                debouncedSave.flush();
              }}
              onKeyDown={(e) => {
                // This input is DOM-nested inside the send form and Base UI's
                // NumberField does not block Enter, so Enter would implicitly
                // submit it and fire off the Proposal. Commit the field instead.
                if (e.key === 'Enter') {
                  e.preventDefault();
                  debouncedSave.flush();
                }
              }}
            />
          )}
        />
      </Field>
    </FormProvider>
  );
};

export default ProposalValidForDaysField;
